import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { StatusBadge } from "@/components/jarvis/StatusBadge";
import { Panel, Field, inputCls, Action, Note } from "./ui";

export default function AiTab() {
  const [info, setInfo] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [test, setTest] = useState(null);
  const [evalRes, setEvalRes] = useState(null);
  const [busy, setBusy] = useState("");
  const [check, setCheck] = useState(null);
  const load = () => api.get("/ai/providers").then((r) => { setInfo(r.data); setCfg(r.data.current); });
  useEffect(() => { load(); }, []);
  if (!info || !cfg) return null;
  const prov = info.providers.find((p) => p.id === cfg.provider) || info.providers[0];
  const save = async () => {
    try { await api.put("/settings", { ai: { provider: cfg.provider, model: cfg.model, emergent_authorized: !!cfg.emergent_authorized } }); toast.success("IA guardada"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const doCheck = async () => { setBusy("check"); try { setCheck((await api.post("/ai/google/check", {}, { timeout: 120000 })).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); } };
  const selectModel = async (m) => { try { await api.put("/settings", { ai: { provider: "google_gemini", model: m } }); toast.success(`Usando ${m} con su clave de Google`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const doTest = async () => { setBusy("test"); try { setTest((await api.post("/ai/test")).data); } finally { setBusy(""); } };
  const doEval = async () => { setBusy("eval"); try { setEvalRes((await api.post("/ai/eval", {}, { timeout: 300000 })).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); } };
  return (
    <div>
      <Panel title="PROVEEDOR DE IA" testId="ai-provider-panel">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Proveedor">
            <select data-testid="ai-provider-select" className={inputCls} value={cfg.provider}
              onChange={(e) => { const p = info.providers.find((x) => x.id === e.target.value); setCfg({ ...cfg, provider: p.id, model: p.models[0] || "" }); }}>
              {info.providers.map((p) => <option key={p.id} value={p.id}>{p.label}{p.env && !p.key_present ? " (sin clave)" : ""}</option>)}
            </select>
          </Field>
          <Field label="Modelo">
            <select data-testid="ai-model-select" className={inputCls} value={cfg.model} onChange={(e) => setCfg({ ...cfg, model: e.target.value })} disabled={!prov.models.length}>
              {prov.models.map((m) => <option key={m}>{m}</option>)}
            </select>
          </Field>
        </div>
        <Note>{prov.billing}</Note>
        <Note>Clave: {prov.env ? `${prov.env} ${prov.key_present ? "presente en el backend" : "NO configurada (cárguela en el .env / variables de Railway, nunca en el chat)"}` : "no aplica"}. {info.note}</Note>
        {cfg.provider === "emergent_gemini" && (
          <label className="mt-3 flex items-start gap-2 border border-amber-500/50 bg-amber-950/20 p-2 text-xs text-amber-200" data-testid="ai-emergent-authorize">
            <input type="checkbox" checked={!!cfg.emergent_authorized} onChange={(e) => setCfg({ ...cfg, emergent_authorized: e.target.checked })} data-testid="ai-emergent-authorize-checkbox" />
            Autorizo explícitamente que JARVIS consuma créditos de mi Universal Key de Emergent. Sin esta autorización no se envía ninguna solicitud.
          </label>
        )}
        <div className="mt-3 flex items-center gap-3 text-xs text-slate-300" data-testid="ai-paid-fallback">
          <input type="checkbox" checked={false} disabled readOnly /> Fallback pago: <b className="text-red-300">desactivado</b> (no se cambia nunca a un servicio pago en silencio)
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Action testId="ai-save-button" onClick={save}>Guardar</Action>
          <Action testId="ai-test-button" variant="ghost" onClick={doTest} disabled={busy === "test"}>{busy === "test" ? "Probando…" : "Probar conexión"}</Action>
        </div>
        {test && <div className="mt-3 text-xs flex items-center gap-2" data-testid="ai-test-result"><StatusBadge status={test.status} />{test.message || `${test.provider} · ${test.model} respondió "${test.sample}"`}</div>}
        {info.last_status && <Note>Último estado: {info.last_status.ok ? "OK" : `${info.last_status.code}: ${info.last_status.message}`}</Note>}
      </Panel>
      <Panel title="SU CLAVE DE GOOGLE (GEMINI API)" testId="ai-google-panel" right={<Action testId="ai-google-check-button" variant="ghost" onClick={doCheck} disabled={busy === "check"}>{busy === "check" ? "Comprobando…" : "Comprobar modelos"}</Action>}>
        <ol className="list-decimal pl-4 text-xs text-slate-300 space-y-1">
          <li>Entre a aistudio.google.com con su cuenta de Google › <b>Get API key</b> › crear clave (proyecto sin facturación = nivel gratuito).</li>
          <li>Cárguela como variable <code className="text-cyan-300">GEMINI_API_KEY</code> en el servidor (Railway › Variables) y reinicie. Nunca en el chat ni en el navegador.</li>
          <li>Pulse «Comprobar modelos»: se hace 1 solicitud mínima por modelo con SU clave (no usa créditos de Emergent).</li>
        </ol>
        {check && (
          <div className="mt-3 text-xs" data-testid="ai-google-check-results">
            {check.message && <p className="text-amber-300">{check.message}</p>}
            {check.results.map((r) => (
              <div key={r.model} className="flex flex-wrap items-center gap-2 border-t border-cyan-500/10 py-1.5">
                <span className="text-slate-200 w-48">{r.model}</span>
                <StatusBadge status={r.status === "disponible" ? "conectado" : r.status === "cupo_agotado" ? "pendiente" : "error"} />
                <span className="text-slate-500 flex-1 truncate">{r.status} · HTTP {r.http} {r.detail}</span>
                {r.status === "disponible" && <Action variant="ghost" testId={`ai-use-model-${r.model}`} onClick={() => selectModel(r.model)}>Usar</Action>}
              </div>
            ))}
            {check.note && <Note>{check.note}</Note>}
          </div>
        )}
      </Panel>
      <Panel title="EVALUACIÓN CON TAREAS REALES" testId="ai-eval-panel" right={<Action testId="ai-eval-button" variant="ghost" onClick={doEval} disabled={busy === "eval"}>{busy === "eval" ? "Evaluando… (≈1 min)" : "Evaluar modelo"}</Action>}>
        <Note>Ejecuta 9 casos (rioplatense, elección de herramienta, aclaración, planificación, no afirmar éxito ante fallo) en modo SIMULADO: ninguna acción se ejecuta. La interpretación de capturas se prueba adjuntando una foto en el chat.</Note>
        {evalRes && (
          <div className="mt-3" data-testid="ai-eval-results">
            <p className="text-sm text-cyan-200 mb-2">Puntaje: {evalRes.score}/{evalRes.total} · {evalRes.model}</p>
            {evalRes.results.map((r) => (
              <div key={r.id} className="border-t border-cyan-500/10 py-2 text-xs">
                <div className="flex gap-2 items-center"><StatusBadge status={r.passed ? "confirmada" : "fallida"} /><span className="text-slate-200">{r.prompt || r.id}</span></div>
                <div className="text-slate-500 mt-1">Herramientas: {(r.tools || []).join(", ") || "ninguna"} · {r.answer || r.error}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
